/**
 * The agent pages, at their address inside the app.
 *
 * Anand, 23 Sep 26: "The agent home is a shareable link and I think that's not
 * best for users who sign up for the GBP growth agent, so let's make the home
 * and other linked pages first class citizens of the app."
 *
 * Every page here has two addresses (and the app's older one) and one component:
 *
 *   /agent/<path>/:token         the shared link, opened without a login
 *   /app/<slug>/<words>          the same page, on the owner's session, in the
 *                                owner's words since PR 6 (APP_PAGE_WORDS:
 *                                ideas is /posts, keywords /growth/local-rank)
 *   /app/l/:locationId/<words>   its old address, still working (it moves to
 *                                the clean one), and what a link names while
 *                                the slug is not known yet (lib/appAddress.ts)
 *
 * Each page's own word (its key, the one listed below and used after /agent/)
 * still opens it under /app on both shapes, for ever: the frame moves it to
 * the page's words, query and hash kept (Shardul, 23 Oct 26).
 *
 * The list is data so that a guard test can hold it against the routes in
 * App.tsx: a token page that is an owner page and has no twin here is a page
 * the signed-in owner cannot reach (tests/agent/unit/agent-app-routes.test.ts).
 */

import { appPagePath } from '@/lib/appAddress';

/**
 * The one table of old and new words: page key -> its words in an /app
 * address (docs/agent-clean-addresses-plan.md, PR 6). It lives in
 * lib/appAddress.ts, which builds and reads every address from it (this file
 * imports that one, so the table cannot live here without the two importing
 * each other), and is exported here beside the page list it covers; a guard
 * test fails when a page in APP_LOCATION_PAGES has no words.
 */
export { APP_PAGE_WORDS } from '@/lib/appAddress';

/** Owner pages: every one of these has an /app twin. */
export const APP_LOCATION_PAGES = [
  'home',
  'activities',
  'notifications',
  'keywords',
  'photos',
  'profile',
  'profile-changes',
  'gbp-services',
  'gbp-attributes',
  'gbp-categories',
  'website',
  // The website's articles, the owner's own and the agent's (Shardul, 27 Oct 26).
  'blogs',
  'listings',
  'performance',
  'leads',
  'contacts',
  'ideas',
  'createpost',
  'new-post',
  'templates',
  'references',
  'audit-report',
  'dashboard',
  'review-generator-setup',
  'review-responder-setup',
  'microsite-setup',
  'offer',
  'subscriptions',
  'subscription',
  'content-settings',
  // The reports the agent kept for the location (Anand, 07 Oct 26).
  'reports',
] as const;

export type AppLocationPage = (typeof APP_LOCATION_PAGES)[number];

/**
 * Token pages that stay token pages, and why. Each one either belongs to a
 * narrow link type the session cannot stand in for (access.ts refuses
 * import_location, enrol_location and reconnect_google to a home link), or is
 * not an owner page at all.
 */
export const TOKEN_ONLY_PAGES: Record<string, string> = {
  connect: 'A connect link does one job: it carries the Google sign-in back to a location that does not exist yet.',
  reconnect: 'A reconnect link is minted for one dead connection and is the only thing allowed to mend it.',
  'social-connect': 'The social sign-in flow reads its own link through the OAuth round trip.',
  'profile-review': 'A profile review link is a profile_review_tokens row, not an agent link.',
  'qa-test-suite': 'The QA board is the testers own surface and is shared as a link on purpose.',
};

/**
 * Where this page lives inside the app: the signed-in person's clean address
 * when the slug is known, else /app/l/<uuid>/<words>, which then moves
 * (appPagePath, docs/agent-clean-addresses-plan.md PR 3), in the page's words
 * on Fly's own hosts (PR 6).
 */
export function appLocationPath(locationId: string, page: string): string {
  return appPagePath(locationId, page);
}

/**
 * Whether this page can be reached at an /app address at all.
 *
 * Both halves matter. APP_LOCATION_PAGES is the list of pages that have an
 * /app twin, and TOKEN_ONLY_PAGES names the ones that never will, each for a
 * written reason. Rewriting a token address for a token-only page would send
 * the reader to a route that does not exist.
 */
export function hasAppTwin(page: string): boolean {
  if (page in TOKEN_ONLY_PAGES) return false;
  return (APP_LOCATION_PAGES as readonly string[]).includes(page);
}

/**
 * A token address turned into its /app twin, for a reader who is signed in.
 *
 * Anand's rule is that the signed-in experience is /app and its funnel, end
 * to end, so somebody with a Fly session should never be walked onto an
 * /agent/<token> address. Several doors used to do exactly that (the start
 * flow's hand-off, the connect page's finish, the connect link's way back),
 * and a signed-in owner standing on /agent/home/<token> then had every page
 * resolve its caller as 'token', which is how a removal threw them out of the
 * app onto /agent/removed (Shardul, QA note 3c13ffbf, 28 Sep 26).
 *
 * `signedIn` defaults to anything but true, so the address comes back exactly
 * as it went in. That is what an owner with no Fly account needs: the token
 * address is the only one they can open, and nothing here touches it.
 *
 * `locationId` is the location whose /app page the reader owns. Without one
 * there is no app address to build, so the token address stands.
 *
 * The token page itself follows the rule too since clean addresses PR 7: a
 * signed-in person who opens /agent/<page>/<token> and may open its location
 * is moved onto this answer (components/agent-shell/SignedInTokenMove.tsx).
 *
 * Everything after the token travels with it, which is the project's rule
 * that a navigation carries the parameters its destination reads: ?view=all
 * opens the All locations tab, ?first=1 shows the first pass running,
 * ?section=billing opens the billing section, and /domain is a step deeper.
 */
export function appAddressForSignedIn(
  url: string | null | undefined,
  locationId: string | null | undefined,
  signedIn?: boolean,
): string {
  const raw = typeof url === 'string' ? url : '';
  if (signedIn !== true || !raw || !locationId) return raw;
  const m = /^\/agent\/([^/?#]+)\/([A-Za-z0-9]{8,64})([/?#].*)?$/.exec(raw);
  if (!m) return raw;
  const [, page, , rest = ''] = m;
  if (!hasAppTwin(page)) return raw;
  return `${appLocationPath(locationId, page)}${rest}`;
}
